import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsCategoryOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50 border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-9 h-9 flex items-center justify-center">
              <i className={`ri-play-circle-fill text-2xl ${isScrolled || !isHome ? 'text-primary-500' : 'text-background-50'}`}></i>
            </div>
            <span
              className={`text-lg font-heading font-bold ${
                isScrolled || !isHome ? 'text-foreground-900' : 'text-background-50'
              }`}
            >
              {siteInfo.name}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isHome
                  ? isScrolled
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-background-50 bg-background-50/20'
                  : location.pathname === '/'
                  ? 'text-primary-500 bg-primary-50'
                  : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              首页
            </Link>

            <div className="relative">
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-500 bg-primary-50'
                    : isScrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                分类浏览
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {isCategoryOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 border border-background-200 rounded-lg py-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? 'text-primary-500 bg-primary-50'
                  : isScrolled || !isHome
                  ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <div className="relative">
              <div className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none">
                <i className={`ri-search-line text-sm ${isScrolled || !isHome ? 'text-foreground-400' : 'text-background-50/60'}`}></i>
              </div>
              <input
                type="text"
                placeholder="搜索视频..."
                className={`pl-10 pr-4 py-2 text-sm rounded-full border outline-none transition-colors w-48 focus:w-64 ${
                  isScrolled || !isHome
                    ? 'bg-background-100 border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300'
                    : 'bg-background-50/15 border-background-50/20 text-background-50 placeholder:text-background-50/50 focus:border-background-50/50'
                }`}
              />
            </div>
          </div>

          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
              isScrolled || !isHome ? 'text-foreground-700' : 'text-background-50'
            }`}
            aria-label="菜单"
          >
            <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-3 space-y-1">
            <Link to="/" className="block px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 whitespace-nowrap cursor-pointer">
              首页
            </Link>
            <p className="px-3 py-2 text-xs font-medium text-foreground-400 uppercase tracking-wider whitespace-nowrap">视频分类</p>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-base`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="block px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 whitespace-nowrap cursor-pointer">
              关于我们
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}